/**
 * Renderer wrappers over the S7 share-management channels (plan §3 IPC table; `src/types/azureStorageFilesApi.ts`):
 * every call mints its own request id, carries a timeout, never throws (an unavailable bridge or a rejected invoke
 * answers a typed `StorageOperationError`) and hands back a handle whose `cancel` sends `azure-storage:cancel` for
 * exactly that id, once — the dialogs cancel their request when they close, like the queue dialogs.
 */
import type { AzureStorageFilesApi, StorageIpcResult } from '../../../../types/azureStorage';
import { cancelStorageRequest, getAzureStorageApi, newStorageRequestId, STORAGE_API_UNAVAILABLE, toStorageOperationError } from '../services/azureStorageApi';

export type ShareApiMember =
  | 'shareCreate' | 'shareDelete' | 'shareProperties' | 'shareQuota' | 'shareSnapshots' | 'shareSnapshotCreate'
  | 'shareSnapshotDelete' | 'fileProperties' | 'shareProtocol';
type FullRequest<M extends ShareApiMember> = Parameters<AzureStorageFilesApi[M]>[0];
export type ShareRequestOf<M extends ShareApiMember> = Omit<FullRequest<M>, 'requestId' | 'timeoutMs'> & { timeoutMs?: number };
export type ShareValueOf<M extends ShareApiMember> = Awaited<ReturnType<AzureStorageFilesApi[M]>> extends StorageIpcResult<infer T> ? T : never;
export type ShareRequestHandle<T> = { requestId: string; promise: Promise<StorageIpcResult<T>>; cancel: () => void };

export const SHARE_REQUEST_TIMEOUT_MS = 30_000;

export function startShareRequest<M extends ShareApiMember>(member: M, request: ShareRequestOf<M>): ShareRequestHandle<ShareValueOf<M>> {
  const requestId = newStorageRequestId();
  const invoke = getAzureStorageApi()?.[member] as ((full: FullRequest<M>) => Promise<StorageIpcResult<ShareValueOf<M>>>) | undefined;
  const full = { timeoutMs: SHARE_REQUEST_TIMEOUT_MS, ...request, requestId } as FullRequest<M>;
  const promise: Promise<StorageIpcResult<ShareValueOf<M>>> = invoke
    ? invoke(full).catch((error: unknown) => ({ ok: false as const, error: toStorageOperationError(error) }))
    : Promise.resolve({ ok: false as const, error: STORAGE_API_UNAVAILABLE });
  let cancelled = false;
  return {
    requestId,
    promise,
    cancel: () => {
      if (cancelled) return;
      cancelled = true;
      void cancelStorageRequest(requestId);
    },
  };
}
